import { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Building2,
  CheckCircle2,
  Globe,
  IdCard,
  Info,
  Mail,
  MapPin,
  Network,
  Pencil,
  Phone,
  ShieldCheck,
  UserCircle2,
} from "lucide-react";

import { PageHeader } from "@/components/admin/shared/PageHeader";
import { PennButton } from "@/components/ui";
import { AccountView } from "@/components/popup/profile/AccountView";
import { useMe } from "@/hooks/useMe";
import { useAppDispatch } from "@/stores/hooks";
import { setUser } from "@/api/redux-toolkit/reducer/authSlice";
import { countryName } from "@penn/web-common";
import { runtimeEnv } from "@/config/runtime-env";
import type { AuthUser, PennRole } from "@/types/auth-types/AuthUser";

/** Permission rows shown under "Active Permissions" — one row per §D.1
 *  boolean. Folding pairs into a single row (the pre-2026-07-17 shape) hid a
 *  granted permission whenever its partner was missing: a role with only
 *  `canManageUsers` rendered "Manage Users/Master Data" unchecked. */
const PERMISSION_ROWS: { label: string; key: keyof PennRole }[] = [
  { label: "Create Applications", key: "canCreateApplications" },
  { label: "Work For Others", key: "canWorkForOthers" },
  { label: "See Prices", key: "canSeePrices" },
  { label: "Generate Quote", key: "canGenerateQuote" },
  { label: "Generate ROI", key: "canGenerateRoi" },
  { label: "Approve Engineering", key: "canApproveEngineering" },
  { label: "Switch Region", key: "canSwitchRegion" },
  { label: "Manage Users", key: "canManageUsers" },
  { label: "Manage Master Data", key: "canManageMasterData" },
];

const dash = (v: string | null | undefined) => (v && v.trim() ? v : "—");

const initialsOf = (user: AuthUser): string => {
  const fromName = `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase();
  return fromName || (user.email[0] ?? "?").toUpperCase();
};

// formatDate is referenced only inside a commented-out HR-compliance note
// (line 239). Keep it commented in source so we can revive it when that block
// is re-enabled.
// const formatDate = (iso: string | null): string => { ... };

export const ProfilePage = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading, isError, error } = useMe();
  const user = data?.user ?? null;
  const [editOpen, setEditOpen] = useState(false);
  // Present only when the central-directory overlay applied and held postal
  // data (see UsersService.overlaySharedProfile) — the address is then edited
  // on the platform profile, not here.
  const hasStructuredAddress = Boolean(
    user?.streetAddress || user?.city || user?.stateRegion || user?.postalCode || user?.country,
  );
  const issuer = runtimeEnv("VITE_OIDC_ISSUER_URL")?.trim().replace(/\/+$/, "");
  const issuerProfileUrl = issuer ? `${issuer}/profile` : null;

  // Keep the Redux auth slice (NavBar avatar + the edit modal's prefill) in
  // sync with the freshly-fetched profile.
  useEffect(() => {
    if (user) dispatch(setUser(user));
  }, [user, dispatch]);

  // Only the GRANTED permissions — the section lists what this role can do,
  // not the full §D.1 matrix with unchecked rows.
  const permissions = useMemo(() => {
    const role = user?.role ?? null;
    if (!role) return [];
    return PERMISSION_ROWS.filter((row) => Boolean(role[row.key])).map((row) => row.label);
  }, [user?.role]);

  return (
    <>
      <PageHeader
        title="Profile Overview"
        subtitle="Manage personal details, organizational context."
        action={
          <PennButton
            variant="primary"
            size="sm"
            onClick={() => setEditOpen(true)}
            disabled={!user}
          >
            <span className="inline-flex items-center gap-1">
              <Pencil size={15} />
              Edit Profile
            </span>
          </PennButton>
        }
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load profile{error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {isLoading && !user ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center text-blue_grey4 text-body">
          Loading profile…
        </div>
      ) : !user ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center text-blue_grey4 text-body">
          No profile information available.
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Card 1: identity + contact + organization + address ── */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            {/* Identity header */}
            <div className="relative px-8 py-7 overflow-hidden">
              <div className="pointer-events-none absolute -top-16 -right-16 h-52 w-52 rounded-full bg-penn-50" />
              <UserCircle2
                size={26}
                strokeWidth={1.6}
                color="gray"
                className="absolute top-6 right-8 text-penn-100"
              />
              <div className="relative flex items-center gap-5">
                <div className="h-20 w-20 shrink-0 rounded-full bg-penn-500 flex items-center justify-center text-white text-2xl font-semibold select-none bg-penn-500">
                  {initialsOf(user)}
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-blue_grey1">
                    {dash(`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim())}
                  </h2>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full bg-penn-100 px-3 py-1 text-penn-700 text-body-semibold">
                      <BadgeCheck size={15} />
                      {dash(user.role?.displayName)}
                    </span>
                    <span className="inline-flex items-center rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-1 font-mono text-blue_grey3 text-small">
                      ID: {user.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-8 py-6">
              <DetailRow icon={<Mail size={16} />} label="Email Address" value={user.email} />
              <DetailRow icon={<Phone size={16} />} label="Mobile Number" value={dash(user.phone)} />
            </div>

            <Divider />

            {/* Organization context */}
            <div className="px-8 py-6">
              <SectionTitle>Organization Context</SectionTitle>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <InfoCell
                  icon={<Building2 size={16} color="var(--color-penn-500)"/>}
                  label="Company"
                  value={dash(user.companyName)}
                  // hint={`Entity ID: ${dash(user.companyId)}`}
                />
                <InfoCell
                  icon={<Network size={16} color="var(--color-penn-500)"/>}
                  label="Department"
                  value={dash(user.department?.displayName)}
                  hint={
                    user.department?.description
                      ? `Branch: ${user.department.description}`
                      : undefined
                  }
                />
                <InfoCell
                  icon={<IdCard size={16} color="var(--color-penn-500)"/>}
                  label="Current Role"
                  value={dash(user.role?.displayName)}
                  hint={user.isInternal ? "Internal user" : "External user"}
                />
                <InfoCell
                  icon={<Globe size={16} color="var(--color-penn-500)"/>}
                  label="Region"
                  value={dash(user.region?.displayName)}
                  hint={
                    user.region?.currencyCode
                      ? `Currency: ${user.region.currencyCode}`
                      : undefined
                  }
                />
              </div>
            </div>

            <Divider />

            {/* Office address */}
            <div className="px-8 py-6">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-penn-100 text-penn-500">
                  <MapPin size={16} color="var(--color-penn-500)"/>
                </span>
                <SectionTitle>Company Address</SectionTitle>
              </div>
              {hasStructuredAddress ? (
                <>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <InfoCell icon={<MapPin size={16} color="var(--color-penn-500)"/>} label="Street address" value={dash(user.streetAddress)} />
                    <InfoCell icon={<MapPin size={16} color="var(--color-penn-500)"/>} label="City" value={dash(user.city)} />
                    <InfoCell icon={<MapPin size={16} color="var(--color-penn-500)"/>} label="State / Region" value={dash(user.stateRegion)} />
                    <InfoCell icon={<MapPin size={16} color="var(--color-penn-500)"/>} label="Postal code" value={dash(user.postalCode)} />
                    <InfoCell icon={<Globe size={16} color="var(--color-penn-500)"/>} label="Country" value={user.country ? countryName(user.country) : "—"} />
                  </div>
                  {issuerProfileUrl && (
                    <a
                      href={issuerProfileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-block text-body text-penn-600 hover:text-penn-700 underline"
                    >
                      Edit on your platform profile
                    </a>
                  )}
                </>
              ) : (
                <div className="mt-4 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-5 py-4 leading-relaxed bg-penn-50 text-body">
                  <AddressBlock address={user.companyAddress} />
                </div>
              )}
            </div>
          </div>

          {/* ── Card 2: permissions + policy notes ── */}
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-8 py-6">
            <div className="flex items-center justify-between">
              <SectionTitle>Active Permissions</SectionTitle>
              <ShieldCheck size={18} color="var(--color-penn-500)" />
            </div>
            {permissions.length === 0 ? (
              <p className="mt-5 text-blue_grey4 text-body">
                No permissions granted to this role.
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {permissions.map((label) => (
                  <div key={label} className="flex items-center gap-3 text-body">
                    <CheckCircle2 size={17} className="text-penn-500" color="var(--color-penn-500)" />
                    <span className="text-blue_grey">{label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-penn-50 px-4 py-3 text-blue_grey2 text-body">
              <Info size={16} className="mt-1 shrink-0 text-penn-400" />
              Permissions are controlled by the Admin .
            </div>

            {/* <div className="mt-4 flex items-start gap-3 rounded-2xl bg-penn-50/70 px-4 py-3 leading-relaxed text-blue_grey3 text-small">
              <Info size={15} className="mt-1 shrink-0 text-blue_grey4" />
              <span>
                <span className="font-semibold text-blue_grey2">Note:</span> Sensitive organizational
                data is encrypted. Any modifications to these fields require a formal request and
                approval from the HR Compliance department.
                {user.lastLoginAt && ` Last verified: ${formatDate(user.lastLoginAt)}.`}
              </span>
            </div> */}
          </div>
        </div>
      )}

      <AccountView open={editOpen} onClose={() => setEditOpen(false)} />
    </>
  );
};

const Divider = () => <div className="h-px bg-gray-100 dark:bg-gray-800" />;

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <p className="uppercase text-blue_grey3 text-small-medium">{children}</p>
);

const AddressBlock = ({ address }: { address: string | null }) => {
  const lines = (address ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return <span className="text-blue_grey">—</span>;
  const [first, ...rest] = lines;
  return (
    <div>
      <p className="font-medium text-blue_grey">{first}</p>
      {rest.map((line, i) => (
        <p key={i} className="text-blue_grey3">
          {line}
        </p>
      ))}
    </div>
  );
};

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div>
    <p className="uppercase text-blue_grey4 text-small-medium">{label}</p>
    <div className="mt-2 flex items-center gap-2 text-blue_grey text-body-medium">
      <span className="text-penn-500">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  </div>
);

const InfoCell = ({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) => (
  <div className="flex gap-3">
    <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-penn-100 text-penn-500">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="uppercase text-blue_grey4 text-small-medium">{label}</p>
      <p className="mt-1 text-blue_grey text-body-medium">{value}</p>
      {hint && <p className="mt-1 text-blue_grey4 text-small">{hint}</p>}
    </div>
  </div>
);
